import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { INTERESTS, MAX_INTERESTS, MIN_INTERESTS } from "@/lib/interests";

// Bottom-sheet editor for the user's profiles.tags. Shared component used by
// the Profile page so people can adjust their interests after onboarding.

interface InterestsEditorProps {
  userId: string;
  initial: string[];
  onClose: () => void;
  onSaved: (tags: string[]) => void;
}

export function InterestsEditor({ userId, initial, onClose, onSaved }: InterestsEditorProps) {
  const known = new Set(INTERESTS.map((i) => i.value));
  const [selected, setSelected] = useState<string[]>(
    () => initial.filter((t) => known.has(t)).slice(0, MAX_INTERESTS)
  );
  const [saving, setSaving] = useState(false);

  // Lock body scroll while the sheet is up so the page behind doesn't drift.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const toggle = (value: string) => {
    setSelected((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= MAX_INTERESTS) return prev;
      return [...prev, value];
    });
  };

  const save = async () => {
    if (selected.length < MIN_INTERESTS) {
      toast.error(`Pick at least ${MIN_INTERESTS} interests`);
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ tags: selected } as never)
        .eq("id", userId);
      if (error) {
        console.error("[InterestsEditor] save failed", error);
        toast.error(error.message ?? "Couldn't save interests");
        setSaving(false);
        return;
      }
      toast.success("Interests updated");
      onSaved(selected);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't save interests";
      toast.error(msg);
      setSaving(false);
    }
  };

  const groups = ["Categories", "Eras", "Aesthetics", "Regions"] as const;
  const meetsMin = selected.length >= MIN_INTERESTS;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "hsl(0 0% 0% / 0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 440,
          background: "hsl(var(--paper))",
          border: "1px solid hsl(var(--ink))",
          borderBottom: "none",
          maxHeight: "85vh",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "1px solid hsl(var(--ink))",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <span className="caps">YOUR INTERESTS</span>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
              fontSize: 22, color: "hsl(var(--ink))", lineHeight: 1.1, marginTop: 2,
            }}>
              What are you hunting?
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              padding: "6px 10px", cursor: "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.15em",
            }}
          >
            ×
          </button>
        </div>

        {/* Counter */}
        <div style={{
          padding: "10px 20px",
          background: meetsMin ? "hsl(var(--cream))" : "hsl(var(--paper-2))",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, letterSpacing: "0.12em", color: "hsl(var(--ink))",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: "1px dashed hsl(var(--ink))",
        }}>
          <span>{selected.length} / {MAX_INTERESTS} PICKED</span>
          <span style={{ color: meetsMin ? "hsl(var(--olive))" : "hsl(var(--rust))" }}>
            {meetsMin ? "READY TO SAVE" : `PICK ${MIN_INTERESTS - selected.length} MORE`}
          </span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {groups.map((group) => (
              <div key={group}>
                <div className="caps" style={{ marginBottom: 6, color: "hsl(var(--ink-2))" }}>
                  {group}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {INTERESTS.filter((i) => i.group === group).map((interest) => {
                    const isSelected = selected.includes(interest.value);
                    const atCap = !isSelected && selected.length >= MAX_INTERESTS;
                    return (
                      <button
                        key={interest.value}
                        type="button"
                        onClick={() => !atCap && toggle(interest.value)}
                        disabled={atCap}
                        aria-pressed={isSelected}
                        style={{
                          padding: "8px 12px",
                          background: isSelected ? "hsl(var(--ink))" : "hsl(var(--cream))",
                          color: isSelected ? "hsl(var(--paper))" : "hsl(var(--ink))",
                          border: "1px solid hsl(var(--ink))",
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11, letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          cursor: atCap ? "not-allowed" : "pointer",
                          opacity: atCap ? 0.4 : 1,
                        }}
                      >
                        {isSelected && "✓ "}{interest.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 14px calc(14px + 72px + env(safe-area-inset-bottom, 0px)) 14px",
          borderTop: "1px solid hsl(var(--ink))",
          display: "flex", gap: 10, background: "hsl(var(--paper))",
        }}>
          <button
            onClick={onClose}
            className="btn"
            style={{ flex: 1, padding: "12px" }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!meetsMin || saving}
            className="btn btn-primary"
            style={{
              flex: 2, padding: "12px",
              opacity: !meetsMin || saving ? 0.5 : 1,
              cursor: !meetsMin || saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "SAVING…" : "Save interests"}
          </button>
        </div>
      </div>
    </div>
  );
}
